import { useEffect, useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../auth'
import { useLang } from '../i18n'
import { usePrefs } from '../prefs'
import { getChartColors } from '../theme/chartPalette'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

// ── Types ─────────────────────────────────────────────────────────────────────

interface CategoryMeta {
  code: string
  display_name_en: string
  display_name_cn: string
}

interface StackedBarEntry {
  group: string
  volumes: Record<string, number>
}

interface MemberRow {
  member_id: string
  display_name: string
  is_group_lead: boolean
  volume: number
  hours: string
  throughput: string
  record_count: number
}

interface GroupTotal {
  volume: number
  hours: string
  throughput: string
}

interface GroupEntry {
  group_name: string
  members: MemberRow[]
  total: GroupTotal
}

interface PivotData {
  categories: CategoryMeta[]
  stacked_bar: StackedBarEntry[]
  cn_table: GroupEntry[]
  us_table: GroupEntry[]
  range: { from: string; to: string }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildApiUrl(tr: TimeRangeValue): string {
  const params = new URLSearchParams({ range: tr.range })
  if (tr.range === 'custom') {
    if (tr.from) params.set('from', tr.from)
    if (tr.to) params.set('to', tr.to)
  }
  return `/api/regional-pivot?${params.toString()}`
}

// ── Member table ──────────────────────────────────────────────────────────────

function MemberTable({
  groups,
  title,
  t,
}: {
  groups: GroupEntry[]
  title: string
  t: (key: string) => string
}) {
  if (groups.length === 0) {
    return (
      <div style={{ flex: '1 1 400px', minWidth: 320 }}>
        <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>{title}</h3>
        <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('regionalNoData')}</div>
      </div>
    )
  }

  const thStyle: React.CSSProperties = {
    padding: '8px 12px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: 13,
    color: 'var(--text-secondary)',
    borderBottom: '2px solid var(--border)',
    whiteSpace: 'nowrap',
  }

  const tdStyle: React.CSSProperties = {
    padding: '7px 12px',
    fontSize: 13,
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border)',
  }

  const totalTdStyle: React.CSSProperties = {
    ...tdStyle,
    fontWeight: 700,
    background: 'var(--bg-page)',
    borderTop: '1px solid var(--border-strong)',
    borderBottom: '2px solid var(--border-strong)',
  }

  return (
    <div style={{ flex: '1 1 400px', minWidth: 320 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>{title}</h3>
      {groups.map((group) => (
        <div key={group.group_name} style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-primary)',
              padding: '6px 12px',
              background: 'var(--bg-page)',
              borderRadius: '4px 4px 0 0',
              borderBottom: '1px solid var(--border)',
            }}
          >
            {group.group_name}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>{t('regionalMember')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('regionalVolume')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('regionalHours')}</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>{t('regionalThroughput')}</th>
                </tr>
              </thead>
              <tbody>
                {group.members.map((m) => (
                  <tr key={m.member_id} style={{ transition: 'background 0.1s' }}>
                    <td style={tdStyle}>
                      {m.display_name}
                      {m.is_group_lead && (
                        <span style={{ marginLeft: 4, fontSize: 12 }}>🎖</span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.volume.toLocaleString()}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.hours}h</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{m.throughput}</td>
                  </tr>
                ))}
                {/* Total row */}
                <tr>
                  <td style={{ ...totalTdStyle, color: 'var(--text-secondary)' }}>{t('regionalTotal')}</td>
                  <td style={{ ...totalTdStyle, textAlign: 'right' }}>
                    {group.total.volume.toLocaleString()}
                  </td>
                  <td style={{ ...totalTdStyle, textAlign: 'right' }}>{group.total.hours}h</td>
                  <td style={{ ...totalTdStyle, textAlign: 'right' }}>{group.total.throughput}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function RegionalPage() {
  const { t } = useLang()
  const { accessToken, user } = useAuth()
  const { theme } = usePrefs()
  const chartColors = getChartColors(theme)

  const [timeRange, setTimeRange] = useState<TimeRangeValue>(defaultTimeRange())
  const [data, setData] = useState<PivotData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const role = user?.role ?? ''

  useEffect(() => {
    if (!accessToken || role === 'member') return
    setLoading(true)
    setError(null)
    fetch(buildApiUrl(timeRange), { headers: { Authorization: `Bearer ${accessToken}` } })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message ?? `HTTP ${res.status}`)
        }
        return res.json() as Promise<PivotData>
      })
      .then((d) => {
        setData(d)
        setLoading(false)
      })
      .catch((err: Error) => {
        setError(err.message ?? 'Error loading data')
        setLoading(false)
      })
  }, [timeRange, role, accessToken])

  if (role === 'member') {
    return (
      <div style={{ padding: 24 }}>
        <p style={{ color: 'var(--danger)', fontWeight: 500 }}>{t('regionalForbidden')}</p>
      </div>
    )
  }

  // Build stacked bar chart data: flatten volumes into top-level keys
  const chartData = (data?.stacked_bar ?? []).map((entry) => ({
    group: entry.group,
    ...entry.volumes,
  }))

  const categories = data?.categories ?? []

  const tooltipStyle = {
    background: chartColors.tooltipBg,
    color: chartColors.tooltipText,
    border: `1px solid ${chartColors.gridColor}`,
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Time range selector */}
      <div style={{ marginBottom: 20 }}>
        <TimeRange value={timeRange} onChange={setTimeRange} />
      </div>

      {loading && (
        <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loading')}</div>
      )}

      {error && (
        <div style={{ color: 'var(--danger)', padding: '8px 0' }}>{error}</div>
      )}

      {data && (
        <>
          {/* Stacked bar chart */}
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
              {t('regionalBarTitle')}
            </h3>
            {chartData.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', padding: 16 }}>{t('regionalNoData')}</div>
            ) : (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 24, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.gridColor} />
                  <XAxis
                    dataKey="group"
                    stroke={chartColors.axisColor}
                    tick={{ fill: chartColors.axisColor }}
                  />
                  <YAxis
                    stroke={chartColors.axisColor}
                    tick={{ fill: chartColors.axisColor }}
                  />
                  <ReTooltip contentStyle={tooltipStyle} />
                  <Legend
                    wrapperStyle={{ color: chartColors.legendColor }}
                    formatter={(value) => {
                      const cat = categories.find((c) => c.code === value)
                      if (!cat) return value
                      return cat.display_name_en
                    }}
                  />
                  {categories.map((cat, idx) => (
                    <Bar
                      key={cat.code}
                      dataKey={cat.code}
                      name={cat.code}
                      stackId="vol"
                      fill={chartColors.categoryColors[idx % chartColors.categoryColors.length]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* CN/US member tables */}
          <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
            <MemberTable
              groups={data.cn_table}
              title={t('regionalCNTitle')}
              t={t}
            />
            <MemberTable
              groups={data.us_table}
              title={t('regionalUSTitle')}
              t={t}
            />
          </div>
        </>
      )}
    </div>
  )
}
